import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FaLinkedinIn, FaWhatsapp, FaTelegramPlane, FaFacebookF } from 'react-icons/fa';

const FooterWrapper = styled.footer`
  background-color: ${props => props.theme.colors.darkGray};
  color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const FooterContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.xl};
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-size: ${props => props.theme.fontSizes.xlarge};
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.white};
`;

const FooterText = styled.p`
  color: ${props => props.theme.colors.lightGray};
  margin-bottom: ${props => props.theme.spacing.md};
  line-height: 1.6;
`;

const SocialIcons = styled.ul`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  list-style: none;
`;

const SocialIconContainer = styled.li`
  margin: 0 10px;
  position: relative;
`;

const Tooltip = styled.span`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  padding: 6px 10px;
  border-radius: 5px;
  opacity: 0;
  visibility: hidden;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &.linkedin {
    background-color: #0077B5;
  }
  
  &.whatsapp {
    background-color: #25D366;
  }
  
  &.telegram {
    background-color: #0088cc;
  }
  
  &.facebook {
    background-color: #1877F2;
  }
  
  ${SocialIconContainer}:hover & {
    opacity: 1;
    visibility: visible;
    top: -50px;
  }
`;

const SocialIcon = styled.a`
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  color: #4d4d4d;
  background-color: #fff;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    box-shadow: 3px 2px 45px 0px rgb(0 0 0 / 12%);
    color: white;
  }
  
  svg {
    position: relative;
    z-index: 1;
    width: 20px;
    height: 20px;
  }
`;

const FilledBackground = styled.div`
  position: absolute;
  top: auto;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 0;
  transition: all 0.3s ease-in-out;
  
  &.linkedin {
    background-color: #0077B5;
  }
  
  &.whatsapp {
    background-color: #25D366;
  }
  
  &.telegram {
    background-color: #0088cc;
  }
  
  &.facebook {
    background-color: #1877F2;
  }
  
  ${SocialIcon}:hover & {
    height: 100%;
  }
`;

const FooterTitle = styled.h4`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  padding-bottom: ${props => props.theme.spacing.xs};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: ${props => props.theme.spacing.sm};
  
  a {
    color: ${props => props.theme.colors.lightGray};
    text-decoration: none;
    transition: all ${props => props.theme.transitions.short};
    display: inline-block;
    
    &:hover {
      color: ${props => props.theme.colors.white};
      transform: translateX(5px);
    }
  }
`;

const Copyright = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  color: ${props => props.theme.colors.mediumGray};
  border-top: 1px solid ${props => props.theme.colors.gray};
  margin-top: ${props => props.theme.spacing.xl};
  font-size: ${props => props.theme.fontSizes.small};
`;

const currentYear = new Date().getFullYear();

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    if (path.endsWith('.html')) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  return (
    <FooterWrapper>
      <FooterContent>
        <FooterSection>
          <FooterLogo>AI AUTOMATION</FooterLogo>
          <FooterText>
            Innovative technology solutions that drive business growth and efficiency.
            We build digital experiences that transform companies.
          </FooterText>
          <SocialIcons>
            <SocialIconContainer>
              <SocialIcon href="https://www.linkedin.com/company/automation-ai-lt/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FilledBackground className="linkedin" />
                <FaLinkedinIn />
              </SocialIcon>
              <Tooltip className="linkedin">LinkedIn</Tooltip>
            </SocialIconContainer>
            
            <SocialIconContainer>
              <SocialIcon href="https://wa.me/37067886746" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <FilledBackground className="whatsapp" />
                <FaWhatsapp />
              </SocialIcon>
              <Tooltip className="whatsapp">WhatsApp</Tooltip>
            </SocialIconContainer>
            
            <SocialIconContainer>
              <SocialIcon href="https://t.me/aiautomationai" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                <FilledBackground className="telegram" />
                <FaTelegramPlane />
              </SocialIcon>
              <Tooltip className="telegram">Telegram</Tooltip>
            </SocialIconContainer>
            
            <SocialIconContainer>
              <SocialIcon href="https://www.facebook.com/profile.php?id=61555313625137" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FilledBackground className="facebook" />
                <FaFacebookF />
              </SocialIcon>
              <Tooltip className="facebook">Facebook</Tooltip>
            </SocialIconContainer>
          </SocialIcons>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Quick Links</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <a onClick={() => handleNavigation('/')} style={{ cursor: 'pointer' }}>Home</a>
            </FooterLink>
            <FooterLink>
              <a onClick={() => handleNavigation('/about')} style={{ cursor: 'pointer' }}>About Us</a>
            </FooterLink>
            <FooterLink>
              <a onClick={() => handleNavigation('/projects')} style={{ cursor: 'pointer' }}>Projects</a>
            </FooterLink>
            <FooterLink>
              <a onClick={() => handleNavigation('/contact')} style={{ cursor: 'pointer' }}>Contact</a>
            </FooterLink>
            <FooterLink>
              <a href="/blog" onClick={(e) => { e.preventDefault(); handleNavigation('/blog'); }} style={{ cursor: 'pointer' }}>Blog</a>
            </FooterLink>
            <FooterLink>
              <a href="/privacy-policy-2.html" style={{ cursor: 'pointer' }}>Privacy Policy</a>
            </FooterLink>
            <FooterLink>
              <a href="/terms-and-conditions.html" style={{ cursor: 'pointer' }}>Terms and Conditions</a>
            </FooterLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Services</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/services/web-development">Web Development</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services/mobile-applications">Mobile Applications</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services/cloud-solutions">Cloud Solutions</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services/ai-machine-learning">AI & Machine Learning</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services/ui-ux-design">UI/UX Design</Link>
            </FooterLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Contact</FooterTitle>
          <FooterText>
            123 Tech Avenue, Innovation District<br />
            San Francisco, CA 94107<br /><br />
            <strong>Phone:</strong> +370 (614) 69 060<br />
            <strong>Email:</strong> contactaiautomation@gmail.com<br /><br />
            <strong>Registration Details:</strong><br />
            MB 306445323<br />
            VAT: LT100017727618<br />
            Reg. Address: Aludarių g. 5-66,<br />
            LT-01113 Vilnius
          </FooterText>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        &copy; {currentYear} AI AUTOMATION. All rights reserved. 
        Designed with ❤️ for innovation.
      </Copyright>
    </FooterWrapper>
  );
};

export default Footer; 